import { type FC, Fragment, useContext, useEffect, useState } from "react"
import { useSelector } from 'react-redux';
import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";

import { api, Booking, IBookingSalesAndPaymentsResult, IPaymentCreate, PaymentTypes } from "../../../../../logic/api";

import { RootState, useAppDispatch } from "../../../../../state/store";
import { PaymentStatus, resetLocalPaymentData, updatePaymentAccountNumber, prepareNewPayment, updatePaymentStatus, INewPayment, initializeReconciledSaleItems } from "../../../../../state/reducers/paymentsReducer";

import { ProgressDialog } from "../../../../../components/notifications/progressDialog/progressDialog";
import ErrorDialog from "../../../../../components/notifications/errorDialog/ErrorDialog";
import PaymentDialogContent from "./PaymentDialogContent";

import { formatCurrency, promisifyApi } from "../../../../../utility/utils";
import { SessionContext } from "../../../../../utility/useSession";
import { usePreferences } from "../../../../../utility/usePreferences";
import { useDate } from "../../../../../utility/useDate";
import PartialPayment from "./PartialPayment";
import SubmitPaymentButton from "./SubmitPaymentButton";
import flex from "../../../../../styles/flex";
import { Trans, useTranslation } from "react-i18next";
import { useReleaseManagement, ReleaseFlag } from "@/utility/feature-management";

interface IPaymentDialogProps {
  booking: Booking;
  saleData: IBookingSalesAndPaymentsResult;
  show: boolean;
  onClose: (updated: boolean) => void;
}

const PaymentDialog: FC<IPaymentDialogProps> = ({
  booking,
  saleData,
  show,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { localizeDate } = useDate();
  const { t } = useTranslation();

  const { outstanding } = saleData;

  const newPayment = useSelector((state: RootState) => state.payments.newPayment);
  const paymentType = useSelector((state: RootState) => state.payments.paymentType);
  const description = useSelector((state: RootState) => state.payments.description);
  const status = useSelector((state: RootState) => state.payments.status);

  const [partialPayment, setPartialPayment] = useState<boolean>(false);

  const session = useContext(SessionContext);
  const { isEligibleAccountPaymentMethod, isEligibleCardPaymentMethod } = usePreferences();
  const addPayment = promisifyApi(api.hooks.productSale.addPayment());
  const updateBooking = promisifyApi(api.hooks.booking.update());

  const isMakePaymentDescriptionMandatoryForComp = useReleaseManagement(
    ReleaseFlag.releaseMakePaymentDescriptionMandatoryForComp
  );
  const isFloatingPointPaymentFixEnabled = useReleaseManagement(
    ReleaseFlag.releaseFloatingPointPaymentFix
  );

  useEffect(() => {
    const { saleItems, reconciliations } = saleData;
    dispatch(resetLocalPaymentData());

    if (show && reconciliations) {
      dispatch(initializeReconciledSaleItems({ saleItems, reconciliations, floatingPointFixEnabled: isFloatingPointPaymentFixEnabled }));
    }

    if (
      booking.leadContact.accountNumber &&
      newPayment.accountNumber !== booking.leadContact.accountNumber
    ) {
      dispatch(updatePaymentAccountNumber(booking.leadContact.accountNumber));
    }
  }, [show]);

  useEffect(() => {
    if (isIPaymentCreate(newPayment) && status === PaymentStatus.Ready) {
      addPayment.send(session.current.context, newPayment).then(() => {
        onClose(true);
      });
    }

    dispatch(updatePaymentStatus(PaymentStatus.Idle));
  }, [status]);

  function isIPaymentCreate(x: INewPayment): x is IPaymentCreate {
    return Boolean(x.bookingId);
  }

  const applyPayment = async () => {
    const needsDescription =
      isMakePaymentDescriptionMandatoryForComp &&
      (paymentType === PaymentTypes.compDiscount || paymentType === PaymentTypes.refund) &&
      (!description || !description.trim());

    if (needsDescription) return;

    if (typeof newPayment.paymentType === "undefined") return;

    if (
      newPayment.accountNumber &&
      newPayment.accountNumber !== booking.leadContact.accountNumber
    ) {
      await updateBooking.send(session.current.context, {
        ...booking,
        leadContact: {
          ...booking.leadContact,
          accountNumber: newPayment.accountNumber,
        },
      });
    }

    const isNotAccountPayment = !isEligibleAccountPaymentMethod(newPayment.paymentType);
    const isNotCardPayment = !isEligibleCardPaymentMethod(newPayment.paymentType);
    const localizedDate = localizeDate(new Date()).toISO();

    dispatch(
      prepareNewPayment({
        booking,
        isNotAccountPayment,
        isNotCardPayment,
        localizedDate,
        partialPayment,
      })
    );
  }

  return (
    <Dialog open={show}>
      <DialogTitle style={{ fontWeight: 700, textAlign: "center" }}>
        {t("payments.addPayment")}
      </DialogTitle>
      <DialogContent>
        <ProgressDialog status={addPayment?.status}></ProgressDialog>
        <ErrorDialog err={addPayment?.err}></ErrorDialog>
        <div style={{ fontSize: 20, color: "red" }}>
          {outstanding >= 0 && (
            <Fragment>
              <b>{formatCurrency(outstanding, session)}</b> {outstanding <= 0 ? t("payments.noOutstanding") : t("payments.outstanding")}
            </Fragment>
          )}
          {outstanding < 0 && (
            <Trans i18nKey="payments.customersOverpaid"
              values={{ amount: formatCurrency(outstanding, session) }}
              components={{ bold: <strong /> }}
            />
          )}
        </div>
        <PaymentDialogContent
          applyPayment={() => applyPayment()}
          onClose={onClose}
          outstanding={saleData.outstanding}
          partialPayment={partialPayment}
          saleData={saleData}
        />
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <div>
            <div style={{ ...flex.flexRows, marginBottom: 20 }}>
              {partialPayment
                &&
                <PartialPayment reconciliations={saleData.reconciliations || []} saleItems={saleData.saleItems} />}
            </div>
            <div style={{ ...flex.flexRows, ...flex.flexFill, width: 520 }}>
              <div style={{ alignContent: "left" }}>
                <Button variant="contained" onClick={() => { onClose(false) }}>
                  {t("general.cancel")}
                </Button>
              </div>
              <div style={{ alignContent: "right", marginLeft: 202 }}>
                <SubmitPaymentButton
                  applyPayment={applyPayment}
                  partialPayment={partialPayment}
                  saleData={saleData}
                />
              </div>
            </div>
          </div>
        </div>
        <div style={{ width: "100%", display: "inline-block", paddingBottom: 8, marginTop: -8 }}>
          <Button size="small" style={{ padding: 8, marginLeft: -4, fontWeight: 400, width: 134, float: 'right' }} onClick={() => setPartialPayment(!partialPayment)}>
            {t("payments.partialPayment")}&nbsp; {partialPayment ? <FiChevronUp size="16" /> : <FiChevronDown size="16" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

  )
};

export default PaymentDialog;

